import type { ApplyCouponInput, CouponInput, CouponTypeValue } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const COUPONS_ROUTES = { LIST: '/cupons' } as const;

const ENDPOINT = '/api/v1/coupons';

export interface Coupon {
  id: string;
  companyId: string;
  code: string;
  description: string | null;
  type: CouponTypeValue;
  value: number;
  minAmount: number | null;
  maxDiscount: number | null;
  startsAt: string | null;
  endsAt: string | null;
  usageLimit: number | null;
  perCustomerLimit: number | null;
  serviceIds: string[];
  isActive: boolean;
  usageCount: number;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppliedCoupon {
  couponId: string;
  code: string;
  description: string | null;
  discount: number;
}

export interface CouponsQuery {
  page: number;
  perPage?: number;
  search?: string;
  status?: 'all' | 'active' | 'inactive';
}

export const couponsApi = {
  list(params: CouponsQuery) {
    const query = new URLSearchParams({ page: String(params.page) });
    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.search) query.set('search', params.search);
    if (params.status && params.status !== 'all') query.set('status', params.status);

    return apiClient.get<{ data: Coupon[]; meta: PaginationMeta }>(`${ENDPOINT}?${query}`);
  },

  create: (input: CouponInput) => apiClient.post<Coupon>(ENDPOINT, input),
  update: (id: string, input: Partial<CouponInput>) =>
    apiClient.patch<Coupon>(`${ENDPOINT}/${id}`, input),
  remove: (id: string) => apiClient.delete<{ message: string }>(`${ENDPOINT}/${id}`),

  /** Valida o codigo na compra e devolve quanto abate - sem gravar nada. */
  apply: (input: ApplyCouponInput) => apiClient.post<AppliedCoupon>(`${ENDPOINT}/apply`, input),
};

const KEYS = { all: ['coupons'] as const };

export function useCoupons(params: CouponsQuery) {
  return useQuery({
    queryKey: [...KEYS.all, params],
    queryFn: () => couponsApi.list(params),
    placeholderData: (previous) => previous,
  });
}

/**
 * Toda mutacao de cupom invalida a lista. O tipo do resultado atravessa: quem
 * cria um cupom costuma precisar do que voltou - o id, para prende-lo em
 * outra coisa - e apagar isso obrigaria a inventar um cast no chamador.
 */
function useCouponMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateCoupon() {
  return useCouponMutation((input: CouponInput) => couponsApi.create(input));
}

export function useUpdateCoupon() {
  return useCouponMutation(({ id, input }: { id: string; input: Partial<CouponInput> }) =>
    couponsApi.update(id, input),
  );
}

export function useDeleteCoupon() {
  return useCouponMutation((id: string) => couponsApi.remove(id));
}

/** Aplicar nao invalida nada: e so uma pergunta ao servidor. */
export function useApplyCoupon() {
  return useMutation({ mutationFn: (input: ApplyCouponInput) => couponsApi.apply(input) });
}
